import styled from '@emotion/styled'
import { useMemo } from 'react'

import { ShiftActivity } from '@bemlo/enums'
import {
  DayjsShift,
  fromDateTimeComponentsWithOnCall,
  getShiftDurations,
} from '@bemlo/schedule'

import { ShiftPreset } from './composable'
import { theme } from '../../constants'
import { Button } from '../../elements'
import { useTranslations } from '../../translate'
import { NewShift } from './composable/components/NewShift'

const Gallery = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
  gap: theme.spacing['1.5'],
  flex: 1,
  button: { height: '100%' },
})

export type ShiftPresetGalleryProps = {
  selected: string | undefined
  presets: ShiftPreset[]
  onClickNew: () => void
  onSelect: (key: string) => void
}

const useShiftPresetDayjs = (
  presets: ShiftPreset[],
): { preset: ShiftPreset; dayjsShift: DayjsShift }[] => {
  const t = useTranslations()

  return useMemo(() => {
    return presets.map((preset) => {
      const date = t.$date().format('YYYY-MM-DD')
      const dateTime = fromDateTimeComponentsWithOnCall(
        date,
        preset.startTime,
        preset.endTime,
        preset.onCallTime,
      )
      const onCallTime =
        dateTime.onCallTime && preset.onCallTime
          ? {
              type: preset.onCallTime.type,
              startDateTime: dateTime.onCallTime.startDateTime,
              endDateTime: dateTime.onCallTime.endDateTime,
              startTime: preset.onCallTime.startTime,
              endTime: preset.onCallTime.endTime,
            }
          : null
      const durations = getShiftDurations({
        ...dateTime,
        onCallTime,
        breakTime: preset.breakTime,
      })
      const dayjsShift: DayjsShift = {
        id: preset.key,
        startDateTime: dateTime.startDateTime,
        endDateTime: dateTime.endDateTime,
        onCallTime,
        durations,
        activity: ShiftActivity.WORK,
        date,
        startTime: preset.startTime,
        endTime: preset.endTime,
        breakTime: preset.breakTime,
        comment: preset.comment,
      }

      return { preset, dayjsShift }
    })
  }, [presets])
}

export const ShiftPresetGallery = (props: ShiftPresetGalleryProps) => {
  const t = useTranslations()
  const dayjsPresets = useShiftPresetDayjs(props.presets)

  return (
    <Gallery>
      {dayjsPresets.map(({ preset, dayjsShift }) => {
        const isSelected = props.selected === preset.key

        return (
          <NewShift
            key={preset.key}
            shift={dayjsShift}
            status={isSelected ? 'ACTIVE_PRESET' : 'PRESET'}
            shiftIds={[preset.key]}
            onClick={() => props.onSelect(preset.key)}
          />
        )
      })}
      <Button
        onClick={props.onClickNew}
        icon="PlusIcon"
        design="transparent-light"
        styles={{ width: '100px', border: theme.border.style.thinDark }}
      >
        {t.shifts.new()}
      </Button>
    </Gallery>
  )
}
